// cSpell:ignore videoconference
import {
  Account,
  CodeableConcept,
  Coding,
  HealthcareService,
  Identifier,
  Location,
  Practitioner,
  PractitionerRole,
  Schedule,
} from 'fhir/r4b';
import type { AppointmentType } from '../types/api/appointment.types';
import type { CanonicalUrl } from '../types/common';
import { ServiceMode, ServiceVisitType } from '../types/common';
import {
  DISCHARGE_SUMMARY_CODE,
  EXPORTED_QUESTIONNAIRE_CODE,
  FAX_PACKET_CODE,
  INSURANCE_CARD_CODE,
  MEDICAL_RECORD_EXPORT_CODE,
  PATIENT_EDUCATION_DOC_TYPE_CODE,
  PATIENT_PHOTO_CODE,
  PHOTO_ID_CARD_CODE,
  PRIVACY_POLICY_CODE,
  RADIOLOGY_REPORT_CODE,
  RECEIPT_CODE,
  SCHOOL_WORK_NOTE_CODE,
  SCHOOL_WORK_NOTE_TEMPLATE_CODE,
  STATEMENT_CODE,
  VISIT_NOTE_SUMMARY_CODE,
} from '../types/data/paperwork/paperwork.constants';
import { ottehrCodeSystemUrl, ottehrExtensionUrl, ottehrIdentifierSystem } from './systemUrls';

// nota bene: some legacy resources could be using 'http' instead of 'https' here, and there are still some string vals out there with http
export const PRIVATE_EXTENSION_BASE_URL = 'https://fhir.zapehr.com/r4/StructureDefinitions';
export const PUBLIC_EXTENSION_BASE_URL = 'https://extensions.fhir.zapehr.com';
export const OYSTEHR_EXTENSION_BASE_URL = 'https://extensions.fhir.oystehr.com';
export const FHIR_ZAPEHR_URL = 'https://fhir.zapehr.com';
const TERMINOLOGY_BASE_URL = 'http://terminology.hl7.org/CodeSystem';

export const SCHEDULE_EXTENSION_URL = 'https://fhir.zapehr.com/r4/StructureDefinitions/schedule';
export const LOCATION_REVIEW_LINK_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/review-link`;
export const PROVIDER_TYPE_EXTENSION_URL = 'https://fhir.zapehr.com/r4/StructureDefinitions/provider-type';
const RCM_TERMINOLOGY_BASE_URL = 'https://terminology.zapehr.com/rcm/cms1500';

export const TIMEZONE_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/timezone';
export const ROOM_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/room';

export const FHIR_BASE_URL = 'https://fhir.ottehr.com';
export const OTTEHR_CODE_SYSTEM_BASE_URL = 'https://fhir.ottehr.com/CodeSystem';

export const FHIR_IDENTIFIER_NPI = 'http://hl7.org/fhir/sid/us-npi';
// https://terminology.hl7.org/en/NamingSystem-CLIA.html
export const FHIR_IDENTIFIER_CLIA = 'http://terminology.hl7.org/NamingSystem/CLIA';
export const FHIR_IDENTIFIER_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0203';
export const FHIR_IDENTIFIER_CODE_NPI = 'NPI';
export const FHIR_IDENTIFIER_CODE_TAX_EMPLOYER = 'NE';
export const FHIR_IDENTIFIER_CODE_TAX_SS = 'SS';
export const FHIR_IDENTIFIER_CODE_TAXONOMY = 'ZZ';
export const FHIR_IDENTIFIER_CODE_STATE_LICENSE = 'SL';
export const OYSTEHR_RCM_PAYER_ID_SYSTEM = 'https://identifiers.fhir.oystehr.com/rcm-payer-id';
export const FRIENDLY_PATIENT_ID_SYSTEM_BASE = 'https://identifiers.fhir.oystehr.com/friendly-patient-id';
export const FHIR_AI_CHAT_CONSENT_CATEGORY_CODE = 'ai-chat';
export const FHIR_HL7_ORG_VALUE_SET_BASE_URL = 'http://hl7.org/fhir/ValueSet';

export const PARTICIPATION_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-ParticipationType';
export const ACCOUNT_TYPE_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/account-type';

export const RAW_X12_EXTENSION_URL = 'https://extensions.fhir.oystehr.com/rcm-raw-x12';
export const RAW_RESPONSE_EXTENSION_URL = `${OYSTEHR_EXTENSION_BASE_URL}/raw-response`;
export const RAW_REQUEST_EXTENSION_URL = `${OYSTEHR_EXTENSION_BASE_URL}/raw-request`;
export const CLAIM_STATUS_RESPONSE_EVENT_SYSTEM = 'https://identifiers.fhir.oystehr.com/rcm-claim-response-event-id';

export const FHIR_EXTENSION = {
  Appointment: {
    additionalInfo: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/additional-information`,
    },
    bookedBy: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/visit-booked-by`,
    },
  },
  Encounter: {
    otherParticipants: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/encounter-other-participants`,
      extension: {
        otherParticipant: {
          url: `${PUBLIC_EXTENSION_BASE_URL}/encounter-other-participant`,
        },
      },
    },
    attestedConsent: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/encounter-attested-consent`,
    },
  },
  EncounterStatusHistory: {
    ottehrVisitStatus: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/visit-status`,
    },
  },
  Location: {
    locationFormPreRelease: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/location-form-pre-release`,
    },
  },
  Patient: {
    weight: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/weight`,
    },
    weightLastUpdated: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/weight-last-updated`,
    },
    authorizedNonLegalGuardians: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/authorized-non-legal-guardians`,
    },
  },
  Paperwork: {
    formListValues: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/form-list-values`,
      extension: {
        formListValue: {
          url: `${PRIVATE_EXTENSION_BASE_URL}/form-list-value`,
        },
      },
    },
    legalTimezone: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/legal-timezone`,
    },
  },
  ContactPoint: {
    erxTelecom: {
      url: 'https://extensions.fhir.oystehr.com/contact-point/telecom-phone-erx',
    },
  },
  MedicationRequest: {
    isRenewal: {
      url: 'https://extensions.fhir.oystehr.com/medication-request/is-renewal',
    },
  },
  MedicationStatement: {
    lastFilledDate: {
      url: ottehrExtensionUrl('medication-last-filled-date'),
    },
    refills: {
      url: ottehrExtensionUrl('medication-refills'),
    },
  },
  InsurancePlan: {
    insuranceRequirements: {
      url: `${PUBLIC_EXTENSION_BASE_URL}/insurance-requirements`,
    },
    notes: {
      url: ottehrExtensionUrl('notes'),
    },
  },
  Coverage: {
    subscriberRelationship: {
      url: `${TERMINOLOGY_BASE_URL}/subscriber-relationship`,
    },
    coverageClass: {
      url: `${TERMINOLOGY_BASE_URL}/coverage-class`,
    },
  },
  Organization: {
    v2_0203: {
      url: `${TERMINOLOGY_BASE_URL}/v2-0203`,
    },
    organizationType: {
      url: `${TERMINOLOGY_BASE_URL}/organization-type`,
    },
  },
  Claim: {
    v3_ActCode: {
      url: `${TERMINOLOGY_BASE_URL}/v3-ActCode`,
    },
    claimConditionCode: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/claim-condition-code`,
    },
    claimInformationCategory: {
      // cSpell:disable-next claiminformationcategory
      url: `${PRIVATE_EXTENSION_BASE_URL}/claiminformationcategory`,
    },
    resubmissionRelationship: {
      url: `${RCM_TERMINOLOGY_BASE_URL}/resubmission-relationship`,
    },
    claimDiagnosesComment: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/claim-diagnoses-comment`,
    },
    revenueCode: {
      url: `${RCM_TERMINOLOGY_BASE_URL}/revenue-code`,
    },
    procedureModifier: {
      url: `${RCM_TERMINOLOGY_BASE_URL}/procedure-modifier`,
    },
    patientPaid: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/patient-paid`,
    },
  },
  AllergyIntolerance: {
    allergyIntoleranceClinical: {
      // cSpell:disable-next allergyintolerance
      url: `${TERMINOLOGY_BASE_URL}/allergyintolerance-clinical`,
    },
  },
  Condition: {
    conditionClinical: {
      url: `${TERMINOLOGY_BASE_URL}/condition-clinical`,
    },
  },
  ServiceRequest: {
    medicationUsed: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/medication-used`,
    },
    bodySide: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/body-side`,
    },
    technique: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/technique`,
    },
    suppliesUsed: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/supplies-used`,
    },
    procedureDetails: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/procedure-details`,
    },
    structuredFacts: { url: `${PRIVATE_EXTENSION_BASE_URL}/procedure-structured-facts` },
    lengthCm: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/length-cm`,
    },
    repairDepth: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/repair-depth`,
    },
    infusionStartTime: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/infusion-start-time`,
    },
    infusionStopTime: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/infusion-stop-time`,
    },
    specimenSent: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/specimen-sent`,
    },
    complications: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/complications`,
    },
    patientResponse: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/patient-response`,
    },
    postInstructions: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/post-instructions`,
    },
    timeSpent: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/time-spent`,
    },
    documentedBy: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/documented-by`,
    },
    consentObtained: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/consent-obtained`,
    },
    externalRadiologyOrder: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/external-radiology-order`,
    },
    radiologyTimeWindow: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/radiology-time-window`,
    },
    radiologySafetyFlag: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/radiology-safety-flag`,
    },
  },
  RelatedPerson: {
    responsiblePartyRelationship: {
      url: `${FHIR_HL7_ORG_VALUE_SET_BASE_URL}/relatedperson-relationshiptype`,
    },
  },
  Observation: {
    examComponentLabel: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/exam-component-label`,
    },
    examComponentGroupLabel: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/exam-component-group-label`,
    },
    examComponentColumnLabel: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/exam-component-column-label`,
    },
    examComponentAbnormal: {
      url: `${PRIVATE_EXTENSION_BASE_URL}/exam-component-abnormal`,
    },
  },
} as const;

export type FHIR_EXTENSION_TYPE = typeof FHIR_EXTENSION;

export const PRACTITIONER_QUALIFICATION_EXTENSION_URL =
  'http://hl7.org/fhir/us/davinci-pdex-plan-net/StructureDefinition/practitioner-qualification';

export const PRACTITIONER_QUALIFICATION_CODE_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v2-0360|2.7';

export const PRACTITIONER_QUALIFICATION_STATE_SYSTEM = 'http://hl7.org/fhir/us/core/ValueSet/us-core-usps-state';

export const SLUG_SYSTEM = `${FHIR_BASE_URL}/r4/slug`;

// Slug values are interpolated raw into FHIR `identifier` search params as
// `${SLUG_SYSTEM}|${slug}` and into patient-facing booking URLs. Restrict to a
// URL-safe shape (letters/digits/hyphens) so a value saved in the admin UI
// can't fail validation when the patient side later searches by it.
export const SLUG_REGEX = /^[a-zA-Z0-9-]+$/;
export const SLUG_VALIDATION_MESSAGE = 'must be a URL-safe slug (letters, digits, hyphens)';
export const isValidSlug = (slug: string): boolean => SLUG_REGEX.test(slug);

/**
 * Derive a URL-safe slug from a human-entered name. Any run of characters that
 * isn't a letter/digit collapses to a single hyphen, and leading/trailing
 * hyphens are trimmed — so the result always satisfies {@link isValidSlug}
 * (or is empty when the name has no URL-safe characters at all, e.g. a purely
 * non-Latin name). Case is preserved to match the existing slug convention
 * (e.g. "NewYork-NY"). Callers must handle the empty-string case (invalid).
 */
export const slugFromName = (name: string): string =>
  name
    .trim()
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Optional admin-editable display name for a PractitionerRole-actored schedule.
 * Stored as a PR.extension valueString. When absent, callers compose a name
 * from the role's referenced Practitioner + Location — see the GroupPage /
 * PractitionerRoleList fallbacks. The field exists to disambiguate two PRs at
 * the same (provider, location) — e.g., a provider's morning intake schedule
 * vs afternoon surgery schedule.
 */
export const SCHEDULE_DISPLAY_NAME_EXTENSION_URL = `${FHIR_BASE_URL}/StructureDefinitions/schedule-display-name`;

/**
 * Per-PractitionerRole "offers every service category" toggle. When `true`,
 * the PR is treated as qualified for every service the slot generator asks
 * about — equivalent to the PR.healthcareService[] list containing every
 * service-category HealthcareService in the system (plus, by definition,
 * every BOOKING_CONFIG compiled-in category, which has no FHIR HS to
 * reference and is otherwise un-opt-into-able). Stored as a boolean PR
 * extension. Absent extension = false (admin opts in explicitly).
 *
 * The analogous group-side mechanism is `GROUP_ALL_LOCATIONS_SYSTEM`.
 */
export const PRACTITIONER_ROLE_ALL_CATEGORIES_EXTENSION_URL = `${FHIR_BASE_URL}/StructureDefinitions/practitioner-role-all-categories`;

export const SERVICE_EXTENSION = 'http://extensions.ottehr.com';

export const AppointmentInsuranceRelatedResourcesExtension = {
  extensionUrl: `${SERVICE_EXTENSION}/insurance-related-resources`,
  primaryCoverage: {
    coverage: { url: `${SERVICE_EXTENSION}/coverage-reference` },
    eligibilityRequest: { url: `${SERVICE_EXTENSION}/coverage-eligibility-request-reference` },
    eligibilityResponse: { url: `${SERVICE_EXTENSION}/coverage-eligibility-response-reference` },
  },
  secondaryCoverage: {
    coverage: { url: `${SERVICE_EXTENSION}/secondary-coverage-reference` },
    eligibilityRequest: { url: `${SERVICE_EXTENSION}/secondary-coverage-eligibility-request-reference` },
    eligibilityResponse: { url: `${SERVICE_EXTENSION}/secondary-coverage-eligibility-response-reference` },
  },
};

export const FHIR_APPOINTMENT_PREPROCESSING_STATUS_SYSTEM = 'appointment-preprocessing-status';

export const FHIR_APPOINTMENT_READY_FOR_PREPROCESSING_TAG = {
  system: FHIR_APPOINTMENT_PREPROCESSING_STATUS_SYSTEM,
  code: 'APPOINTMENT_READY_FOR_PREPROCESSING',
};

export const FHIR_APPOINTMENT_PREPROCESSED_TAG = {
  system: FHIR_APPOINTMENT_PREPROCESSING_STATUS_SYSTEM,
  code: 'APPOINTMENT_PREPROCESSED',
};

export const FHIR_APPOINTMENT_INTAKE_HARVESTING_COMPLETED_TAG = {
  system: 'appointment-harvesting-module-status',
  code: 'SUB_INTAKE_HARVEST_TASK_COMPLETE',
};

export const APPOINTMENT_LOCKED_META_TAG_SYSTEM = 'appointment-locked-status';
export const APPOINTMENT_LOCKED_META_TAG = {
  system: APPOINTMENT_LOCKED_META_TAG_SYSTEM,
  code: 'APPOINTMENT_LOCKED',
};

export const ENCOUNTER_LOCKED_META_TAG_SYSTEM = 'encounter-locked-status';
export const ENCOUNTER_LOCKED_META_TAG = {
  system: ENCOUNTER_LOCKED_META_TAG_SYSTEM,
  code: 'ENCOUNTER_LOCKED',
};

export const FHIR_ENCOUNTER_ERX_PATIENT_SYNC_SYSTEM = 'encounter-erx-sync-status';
export const FHIR_ENCOUNTER_ERX_PATIENT_SYNC_CODE = 'ERX_PATIENT_SYNCED';
export const FHIR_ENCOUNTER_ERX_PATIENT_SYNC_TAG = {
  system: FHIR_ENCOUNTER_ERX_PATIENT_SYNC_SYSTEM,
  code: FHIR_ENCOUNTER_ERX_PATIENT_SYNC_CODE,
};

export const ERX_MEDICATION_META_TAG_CODE = 'erx-medication';

export const FHIR_APPOINTMENT_TYPE_MAP: Record<string, AppointmentType> = {
  walkin: 'walk-in',
  prebook: 'pre-booked',
  posttelemed: 'post-telemed',
};

export const SERVICE_MODE_SYSTEM = 'http://hl7.org/fhir/service-mode';
export const SCHEDULE_STRATEGY_SYSTEM = `${PRIVATE_EXTENSION_BASE_URL}/healthcare-service-schedule-strategy`;

export const ServiceModeCoding = {
  inPerson: {
    system: SERVICE_MODE_SYSTEM,
    code: 'in-person',
    display: 'In Person',
    fullParam: `${SERVICE_MODE_SYSTEM}|in-person`,
  },
  telephone: {
    system: SERVICE_MODE_SYSTEM,
    code: 'telephone',
    display: 'Telephone',
    fullParam: `${SERVICE_MODE_SYSTEM}|telephone`,
  },
  videoConference: {
    system: SERVICE_MODE_SYSTEM,
    code: 'videoconference',
    display: 'Video Conference',
    fullParam: `${SERVICE_MODE_SYSTEM}|videoconference`,
  },
  chat: {
    system: SERVICE_MODE_SYSTEM,
    code: 'chat',
    display: 'Chat/Messaging',
    fullParam: `${SERVICE_MODE_SYSTEM}|chat`,
  },
};

export const SCHEDULE_NUM_DAYS = 2;

export enum ScheduleStrategy {
  owns = 'owns',
  poolsLocations = 'pools-locations',
  poolsProviders = 'pools-providers',
  poolsAll = 'pools-all',
}

export interface ScheduleAndOwner {
  schedule: Schedule;
  owner: Location | Practitioner | PractitionerRole | HealthcareService;
}

interface BaseScheduleResponse {
  scheduleList: ScheduleAndOwner[];
}
interface ScheduleMetaData {
  type: 'location' | 'provider' | 'group';
  strategy?: ScheduleStrategy;
}
export interface BookableScheduleData extends BaseScheduleResponse {
  metadata: ScheduleMetaData;
}

export const ScheduleStrategyCoding = {
  owns: {
    system: SCHEDULE_STRATEGY_SYSTEM,
    code: 'owns',
    display: 'Owns',
    fullParam: `${SCHEDULE_STRATEGY_SYSTEM}|owns`,
  },
  poolsLocations: {
    system: SCHEDULE_STRATEGY_SYSTEM,
    code: 'pools-locations',
    display: 'Pools Locations',
    fullParam: `${SCHEDULE_STRATEGY_SYSTEM}|pools-locations`,
  },
  poolsProviders: {
    system: SCHEDULE_STRATEGY_SYSTEM,
    code: 'pools-providers',
    display: 'Pools Providers',
    fullParam: `${SCHEDULE_STRATEGY_SYSTEM}|pools-providers`,
  },
  poolsAll: {
    system: SCHEDULE_STRATEGY_SYSTEM,
    code: 'pools-all',
    display: 'Pools All',
    fullParam: `${SCHEDULE_STRATEGY_SYSTEM}|pools-all`,
  },
};

// ── HealthcareService characterization (service categories + groups) ─────────
//
// Constants below characterize HealthcareService resources used in the
// group-scheduling rework: service-category catalog entries (tagged with
// SERVICE_CATEGORY_TAG.meta) and group resources. Each `*_SYSTEM` is the
// code-system URL for one HealthcareService.characteristic dimension; the
// adjacent `*Coding` object provides typed shapes with `fullParam` ready for
// FHIR _filter queries.
//
// Note on parallel concepts: a separate ServiceModeCoding above uses the HL7
// standard system for location/practitioner mode tracking. ServiceCategory-
// ModeCoding here uses an ottehr-namespaced system derived from the product-
// level ServiceMode enum.

/** meta.tag identifying a HealthcareService as a service-category catalog entry. */
export const SERVICE_CATEGORY_TAG = {
  system: ottehrCodeSystemUrl('healthcare-service-type'),
  code: 'booking-service-category',
};

/** Code system for service-category codes (e.g. 'urgent-care', 'botox'). Used in HealthcareService.type[] codings. */
export const SERVICE_CATEGORY_SYSTEM = ottehrCodeSystemUrl('service-category');

/** Extension URL for the JSON-blob runtime config on service-category resources. */
export const SERVICE_CATEGORY_CONFIG_EXTENSION_URL = ottehrExtensionUrl('service-category-config');

/** meta.tag identifying a Questionnaire as practice-managed (admin-authored custom form). */
export const PRACTICE_MANAGED_QUESTIONNAIRE_TAG = {
  system: ottehrCodeSystemUrl('questionnaire-type'),
  code: 'practice-managed',
};

/** meta.tag system naming where a practice-managed form's answers appear; no tag means Visit Details. */
export const FORM_PLACEMENT_TAG_SYSTEM = ottehrCodeSystemUrl('form-placement');

// applied (alongside the practice-managed tag) to questionnaires uploaded as raw FHIR JSON.
// these are read only in the admin portal: they can only be updated by uploading a new version
export const JSON_IMPORT_QUESTIONNAIRE_TAG = {
  system: ottehrCodeSystemUrl('questionnaire-type'),
  code: 'json-import',
};

/** meta.tag identifying a paperwork flow Questionnaire. */
export const PAPERWORK_FLOW_TAG = {
  system: `${PRIVATE_EXTENSION_BASE_URL}/flow-type`,
  code: 'paperwork-flow',
};

/**
 * meta.tag system stamped on a paperwork flow Questionnaire for each service it's applied to that
 * doesn't (yet) exist as a HealthcareService resource — code is the service id. Lets a flow target a
 * service before its catalog entry is created, without a HealthcareService to stamp.
 */
export const SYSTEM_MANAGED_SERVICE_TAG_SYSTEM = ottehrCodeSystemUrl('system-managed-service');

/**
 * Extension on a paperwork flow Questionnaire recording a visit mode it targets (valueCode
 * 'in-person' | 'virtual'), repeated once per targeted mode.
 */
export const PAPERWORK_FLOW_MODE_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/paperwork-flow-mode`;

/**
 * Extensions stamped on a service-category HealthcareService pointing at the paperwork flow
 * Questionnaire to present for a given visit mode (valueCanonical `url|version`). At most one of each
 * per HealthcareService; a booking resolves the extension matching its visit mode.
 */
export const PAPERWORK_FLOW_INPERSON_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/paperwork-flow-questionnaire-inperson`;
export const PAPERWORK_FLOW_VIRTUAL_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/paperwork-flow-questionnaire-virtual`;

/** meta.tag identifying how a one off QR was triggered */
export const QR_DISTRIBUTION_TAG = {
  system: ottehrCodeSystemUrl('qr-distribution'),
  code: 'practitioner', // right now only triggered by users sending from visit details but this could be expanded in the future
};

/**
 * meta.tag identifying a QuestionnaireResponse as the patient's intake paperwork response — the one
 * created at booking, whether it points at the default intake Questionnaire or a paperwork flow.
 * Readers use this (in addition to the legacy intake-paperwork canonical-URL match) to recognize
 * flow-backed paperwork QRs, whose canonical is the flow's url and does not contain the intake URLs.
 */
export const INTAKE_PAPERWORK_QR_TAG = {
  system: ottehrCodeSystemUrl('questionnaire-response-type'),
  code: 'intake-paperwork',
};

/** meta.tag system for who sent triggered QR send, code is expected to be practitioner reference and display is expected to be a human readable name */
export const QR_SENT_BY_SYSTEM = ottehrCodeSystemUrl('qr-practitioner-distribution-by');

// ── Service-category characteristic systems (one per dimension) ─────────────

/** Service-mode characteristic for a service-category HealthcareService. Codes match the ServiceMode enum. */
export const SERVICE_CATEGORY_MODE_SYSTEM = ottehrCodeSystemUrl('service-category-mode');
export const ServiceCategoryModeCoding = {
  inPerson: {
    system: SERVICE_CATEGORY_MODE_SYSTEM,
    code: ServiceMode['in-person'],
    display: 'In Person',
    fullParam: `${SERVICE_CATEGORY_MODE_SYSTEM}|${ServiceMode['in-person']}`,
  },
  virtual: {
    system: SERVICE_CATEGORY_MODE_SYSTEM,
    code: ServiceMode.virtual,
    display: 'Virtual',
    fullParam: `${SERVICE_CATEGORY_MODE_SYSTEM}|${ServiceMode.virtual}`,
  },
};

/** Visit-type-capability characteristic for a service-category HealthcareService. Codes match the ServiceVisitType enum. */
export const SERVICE_CATEGORY_VISIT_TYPE_SYSTEM = ottehrCodeSystemUrl('service-category-visit-type');
export const ServiceCategoryVisitTypeCoding = {
  prebook: {
    system: SERVICE_CATEGORY_VISIT_TYPE_SYSTEM,
    code: ServiceVisitType.prebook,
    display: 'Prebook',
    fullParam: `${SERVICE_CATEGORY_VISIT_TYPE_SYSTEM}|${ServiceVisitType.prebook}`,
  },
  walkIn: {
    system: SERVICE_CATEGORY_VISIT_TYPE_SYSTEM,
    code: ServiceVisitType['walk-in'],
    display: 'Walk-In',
    fullParam: `${SERVICE_CATEGORY_VISIT_TYPE_SYSTEM}|${ServiceVisitType['walk-in']}`,
  },
};

/**
 * Duration-minutes characteristic system for a service-category HealthcareService.
 * Values are runtime-configurable (admins set the minutes per service), so this
 * is a system constant only — call sites build the coding inline with
 * `{ system: SERVICE_CATEGORY_DURATION_MINUTES_SYSTEM, code: String(minutes), display: '<n> min' }`.
 */
export const SERVICE_CATEGORY_DURATION_MINUTES_SYSTEM = ottehrCodeSystemUrl('service-category-duration-minutes');

/**
 * Default slot duration in minutes when a service-category HealthcareService
 * doesn't carry a SERVICE_CATEGORY_DURATION_MINUTES_SYSTEM characteristic.
 * Used as a soft fallback for legacy / partially-configured records. New
 * admin saves always set the field explicitly.
 */
export const DEFAULT_SERVICE_CATEGORY_DURATION_MINUTES = 15;

/** Cadence-minutes characteristic system for a service-category HealthcareService. Same call-site shape as duration-minutes. */
export const SERVICE_CATEGORY_CADENCE_MINUTES_SYSTEM = ottehrCodeSystemUrl('service-category-cadence-minutes');

// ── Group characteristic systems ────────────────────────────────────────────

/** Assignment-mode characteristic for a group HealthcareService: 'anonymous' (default) vs 'provider'. */
export const GROUP_ASSIGNMENT_MODE_SYSTEM = ottehrCodeSystemUrl('group-assignment-mode');
export const GroupAssignmentModeCoding = {
  anonymous: {
    system: GROUP_ASSIGNMENT_MODE_SYSTEM,
    code: 'anonymous',
    display: 'Anonymous',
    fullParam: `${GROUP_ASSIGNMENT_MODE_SYSTEM}|anonymous`,
  },
  provider: {
    system: GROUP_ASSIGNMENT_MODE_SYSTEM,
    code: 'provider',
    display: 'Provider',
    fullParam: `${GROUP_ASSIGNMENT_MODE_SYSTEM}|provider`,
  },
};

/**
 * All-locations characteristic for a group HealthcareService: 'true' | 'false'.
 * When 'true', the group pools from every active PractitionerRole in the
 * system (not constrained to the group's `.location[]` entries). Set on the
 * group admin form via a single toggle.
 */
export const GROUP_ALL_LOCATIONS_SYSTEM = ottehrCodeSystemUrl('group-all-locations');
export const GroupAllLocationsCoding = {
  true: {
    system: GROUP_ALL_LOCATIONS_SYSTEM,
    code: 'true',
    display: 'All locations',
    fullParam: `${GROUP_ALL_LOCATIONS_SYSTEM}|true`,
  },
  false: {
    system: GROUP_ALL_LOCATIONS_SYSTEM,
    code: 'false',
    display: 'Specific locations',
    fullParam: `${GROUP_ALL_LOCATIONS_SYSTEM}|false`,
  },
};

// ── Form templates ──────────────────────────────────────────────────────────

/**
 * Broad classification for admin-authored fillable PDF form templates.
 *
 * Set membership lives on `DocumentReference.category` (0..*) rather than `type` (0..1) so the single
 * `type` slot stays free for the document's actual kind.
 */
export const DOCUMENT_CATEGORY_SYSTEM = ottehrCodeSystemUrl('document-category');

/**
 * Every code this system defines, in one place.
 *
 * Each code is used twice below — once in the `Coding` written to a resource and once in the `system|code`
 * string used to search for it — so naming it here is what stops the two drifting apart.
 */
export const DOCUMENT_CATEGORY_CODES = {
  formTemplate: 'form-template',
  formInstance: 'form-instance',
} as const;

/**
 * Typed as `Coding` deliberately: this object is written straight into a FHIR resource, and the server
 * rejects any property that is not part of the datatype. Keeping the search-parameter string separate
 * (below) means a convenience field cannot accidentally ride along into a resource.
 */
export const FORM_TEMPLATE_CATEGORY_CODING: Coding = {
  system: DOCUMENT_CATEGORY_SYSTEM,
  code: DOCUMENT_CATEGORY_CODES.formTemplate,
  display: 'Form Template',
};

/** `system|code` form, for `category=` searches. Never written to a resource. */
export const FORM_TEMPLATE_CATEGORY_SEARCH_PARAM = `${DOCUMENT_CATEGORY_SYSTEM}|${DOCUMENT_CATEGORY_CODES.formTemplate}`;

/**
 * Marks a document that should stay out of the patient's document list while it is still `preliminary`.
 *
 * A meta tag rather than a rule about categories, so the documents explorer does not have to know which
 * kinds of document have drafts worth hiding. A workflow that produces working copies opts in by tagging
 * them; everything else is unaffected, and the explorer's filter never changes.
 *
 * The status is still what does the hiding. `preliminary` alone is not enough to justify it — an unreviewed
 * lab result carries that status and a clinician is waiting on it — so the tag says only "this particular
 * document is not worth reading until it is finished".
 */
export const HIDE_WHILE_PRELIMINARY_TAG: Coding = {
  system: ottehrCodeSystemUrl('document-visibility'),
  code: 'hide-while-preliminary',
  display: 'Hide while preliminary',
};

/**
 * A prefilled copy of a template, produced for one encounter.
 *
 * Distinct from the template category so a chart listing cannot pick up templates, and a template listing
 * cannot pick up instances — they live in different buckets and mean different things.
 */
export const FORM_INSTANCE_CATEGORY_CODING: Coding = {
  system: DOCUMENT_CATEGORY_SYSTEM,
  code: DOCUMENT_CATEGORY_CODES.formInstance,
  display: 'Filled Form',
};

/** `system|code` form, for `category=` searches. Never written to a resource. */
export const FORM_INSTANCE_CATEGORY_SEARCH_PARAM = `${DOCUMENT_CATEGORY_SYSTEM}|${DOCUMENT_CATEGORY_CODES.formInstance}`;

/**
 * Whether a template's PDF has fillable fields, recorded as a second `category` coding.
 *
 * This lives in `category` rather than alongside the field inventory in an extension because listings
 * need it and extensions do not survive an `_elements` projection — FHIR selects whole elements, so
 * asking for the fillability flag would drag every template's full field inventory back with it. Being
 * 0..*, `category` accommodates this without displacing the set-membership coding, and it stays
 * searchable if a listing ever wants to filter on it.
 */
export const FORM_TEMPLATE_FILLABILITY_SYSTEM = ottehrCodeSystemUrl('form-template-fillability');
export const FormTemplateFillability = {
  fillable: 'fillable',
  printable: 'printable',
} as const;

/** Stable per-template business key, so a template survives having its PDF replaced. */
export const FORM_TEMPLATE_IDENTIFIER_SYSTEM = ottehrIdentifierSystem('form-template');

/**
 * Field inventory extracted from the template PDF, stored as JSON.
 *
 * A cache, not a source of truth — it is always re-derivable from the file. It lives in an extension so
 * list queries can leave it behind via `_elements`; a 200-field form is not something a listing should
 * carry.
 */
export const FORM_TEMPLATE_FIELD_INVENTORY_EXTENSION_URL = ottehrExtensionUrl('form-template-field-inventory');

/** Classification from the last analysis of the template PDF, so the UI can explain what a template supports. */
export const FORM_TEMPLATE_ANALYSIS_EXTENSION_URL = ottehrExtensionUrl('form-template-analysis');

/**
 * Admin-authored bindings from chart context to the template's PDF fields, stored as JSON.
 *
 * Unlike the inventory this is not re-derivable — it is the authored artifact the whole feature exists
 * to produce, so it is never rewritten except by an explicit save.
 */
export const FORM_TEMPLATE_MAPPING_EXTENSION_URL = ottehrExtensionUrl('form-template-mapping');

/**
 * Where an imported template's PDF was fetched from.
 *
 * Provenance only — the stored copy is the template. Kept so an admin can see where a form came from, and
 * so a future check for a newer published version has somewhere to look.
 */

export const BUCKET_NAMES = {
  VISIT_NOTES: 'visit-notes',
  CONSENT_FORMS: 'consent-forms',
  PRIVACY_POLICY: 'privacy-policy',
  INSURANCE_CARDS: 'insurance-cards',
  PHOTO_ID_CARDS: 'photo-id-cards',
  PATIENT_PHOTOS: 'patient-photos',
  SCHOOL_WORK_NOTES: 'school-work-notes',
  SCHOOL_WORK_NOTE_TEMPLATES: 'school-work-note-templates',
  LABS: 'labs',
  RECEIPTS: 'receipts',
  PAPERWORK: 'exported-questionnaires',
  DISCHARGE_SUMMARIES: 'discharge-summaries',
  STATEMENTS: 'statements',
  PATIENT_EDUCATION: 'patient-education',
  PATIENT_EDUCATION_ADMIN: 'patient-education-admin',
  /** Admin-authored fillable PDF form templates. Org-level: no patient path segment. */
  FORM_TEMPLATES: 'pdf-form-templates',
  /** Prefilled copies of those templates, one per encounter. Patient-scoped, unlike the templates. */
  FORM_INSTANCES: 'pdf-form-instances',
  RADIOLOGY_REPORTS: 'radiology-reports',
  REPORTS: 'invoiceable-patients-reports',
  BILLING_CLAIM_EXPORTS: 'billing-claim-exports',
  CUSTOM_FOLDERS: 'patient-docs-custom-folders',
  MEDICAL_RECORD_EXPORTS: 'medical-record-exports',
  FAXES: 'faxes',
} as const;

export type BucketName = (typeof BUCKET_NAMES)[keyof typeof BUCKET_NAMES];
export interface ListConfig {
  title: string;
  display: string;
  documentTypeCode: string | string[];
}

export const FOLDERS_CONFIG: ListConfig[] = [
  {
    title: BUCKET_NAMES.VISIT_NOTES,
    display: 'Visit Notes',
    documentTypeCode: VISIT_NOTE_SUMMARY_CODE,
  },
  {
    title: BUCKET_NAMES.CONSENT_FORMS,
    display: 'Consent Forms',
    documentTypeCode: 'patient-registration', // PAPERWORK_CONSENT_CODE_UNIQUE.code
  },
  {
    title: BUCKET_NAMES.PRIVACY_POLICY,
    display: 'Privacy Policy',
    documentTypeCode: PRIVACY_POLICY_CODE,
  },
  {
    title: BUCKET_NAMES.INSURANCE_CARDS,
    display: 'Insurance Cards',
    documentTypeCode: INSURANCE_CARD_CODE,
  },
  {
    title: BUCKET_NAMES.PHOTO_ID_CARDS,
    display: 'Photo ID',
    documentTypeCode: PHOTO_ID_CARD_CODE,
  },
  {
    title: BUCKET_NAMES.PATIENT_PHOTOS,
    display: 'Photos',
    documentTypeCode: PATIENT_PHOTO_CODE,
  },
  {
    title: BUCKET_NAMES.SCHOOL_WORK_NOTES,
    display: 'School/Work Notes',
    documentTypeCode: SCHOOL_WORK_NOTE_CODE,
  },
  {
    title: BUCKET_NAMES.SCHOOL_WORK_NOTE_TEMPLATES,
    display: 'School/Work Note templates',
    documentTypeCode: SCHOOL_WORK_NOTE_TEMPLATE_CODE,
  },
  {
    title: BUCKET_NAMES.LABS,
    display: 'Labs',
    documentTypeCode: [
      '51991-8', // LAB_ORDER_DOC_REF_CODING_CODE.code - external lab ottehr generated order form and eReqs
      '11502-2', // LAB_RESULT_DOC_REF_CODING_CODE.code - lab results -- includes lab-generated and ottehr generated for external, as well as internal results
      'specimen-container-label', // EXTERNAL_LAB_LABEL_DOC_REF_DOCTYPE.code
      'external-lab-abn', // OYSTEHR_ABN_DOC_REF_CODING_UNIQUE.code
    ],
  },
  {
    title: BUCKET_NAMES.RECEIPTS,
    display: 'Receipts',
    documentTypeCode: RECEIPT_CODE,
  },
  {
    title: BUCKET_NAMES.PAPERWORK,
    display: 'Paperwork',
    documentTypeCode: EXPORTED_QUESTIONNAIRE_CODE,
  },
  {
    title: BUCKET_NAMES.DISCHARGE_SUMMARIES,
    display: 'Discharge Summary',
    documentTypeCode: DISCHARGE_SUMMARY_CODE,
  },
  {
    title: BUCKET_NAMES.STATEMENTS,
    display: 'Statements',
    documentTypeCode: STATEMENT_CODE,
  },
  {
    title: BUCKET_NAMES.PATIENT_EDUCATION,
    display: 'Patient Education',
    documentTypeCode: PATIENT_EDUCATION_DOC_TYPE_CODE,
  },
  {
    title: BUCKET_NAMES.RADIOLOGY_REPORTS,
    display: 'Radiology Reports',
    documentTypeCode: RADIOLOGY_REPORT_CODE,
  },
  {
    title: BUCKET_NAMES.MEDICAL_RECORD_EXPORTS,
    display: 'Medical Records',
    documentTypeCode: MEDICAL_RECORD_EXPORT_CODE,
  },
  {
    title: BUCKET_NAMES.FAXES,
    display: 'Faxes',
    documentTypeCode: FAX_PACKET_CODE,
  },
];

// Relationships deliberately absent here fall back to 'other' at the call sites. 'Employee' is one:
// the HL7 subscriber-relationship CodeSystem has no employee code, and mapping it here would give
// 'other' two keys, making the reverse lookup in the billing rules engine order-dependent. The
// coding's `display` carries the original label either way.
export const SUBSCRIBER_RELATIONSHIP_CODE_MAP: Record<string, string> = {
  Child: 'child',
  Parent: 'parent',
  Spouse: 'spouse',
  'Common Law Spouse': 'common',
  Other: 'other',
  Self: 'self',
  'Injured Party': 'injured',
};

// Canonical set of subscriber/policy-holder relationships to the patient, shared across the
// clinical EHR and billing app so the values stay aligned. Keep in step with
// `relationshipToInsuredOptions` in ottehr-config/value-sets, which drives the paperwork dropdown.
export const SUBSCRIBER_RELATIONSHIPS = [
  'Self',
  'Child',
  'Parent',
  'Spouse',
  'Common Law Spouse',
  'Injured Party',
  'Employee',
  'Other',
] as const;
export type SubscriberRelationship = (typeof SUBSCRIBER_RELATIONSHIPS)[number];

// CodeSystem for Coverage.relationship (the subscriber's relationship to the beneficiary).
export const SUBSCRIBER_RELATIONSHIP_SYSTEM = 'http://terminology.hl7.org/CodeSystem/subscriber-relationship';
// System used on RelatedPerson.relationship coding for coverage subscribers / policy holders.
export const RELATED_PERSON_RELATIONSHIP_SYSTEM = 'http://hl7.org/fhir/ValueSet/relatedperson-relationshiptype';

export const BIRTH_SEXES = ['Male', 'Female', 'Intersex'] as const;
export type BirthSex = (typeof BIRTH_SEXES)[number];

// this is required by US Core
// https://build.fhir.org/ig/HL7/US-Core/StructureDefinition-us-core-coverage-definitions.html#key_Coverage.identifier:memberid.type
export const COVERAGE_MEMBER_IDENTIFIER_BASE: Partial<Identifier> = {
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
        code: 'MB',
        display: 'Member Number',
      },
    ],
  },
};

export const PATIENT_BILLING_ACCOUNT_TYPE: Account['type'] = {
  coding: [
    {
      system: ACCOUNT_TYPE_CODE_SYSTEM,
      code: 'PBILLACCT',
      display: 'patient billing account',
    },
  ],
};

export const WORKERS_COMP_ACCOUNT_TYPE: Account['type'] = {
  coding: [
    {
      system: ACCOUNT_TYPE_CODE_SYSTEM,
      code: 'WCOMPACCT',
      display: 'worker compensation account',
    },
  ],
};

export const OCCUPATIONAL_MEDICINE_ACCOUNT_TYPE: Account['type'] = {
  coding: [
    {
      system: ACCOUNT_TYPE_CODE_SYSTEM,
      code: 'OCCUPATIONALMEDICINEACCT',
      display: 'occupational medicine account',
    },
  ],
};

export const OTTEHR_QUESTIONNAIRE_EXTENSION_KEYS = {
  acceptsMultipleAnswers: `${PRIVATE_EXTENSION_BASE_URL}/accepts-multiple-answers`,
  alwaysFilter: `${PRIVATE_EXTENSION_BASE_URL}/always-filter`,
  attachmentText: `${PRIVATE_EXTENSION_BASE_URL}/attachment-text`,
  autofillFromWhenDisabled: `${PRIVATE_EXTENSION_BASE_URL}/fill-from-when-disabled`,
  // standard SDC extension (not a private one): the item's value is derived from other answers
  calculatedExpression: 'http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire-calculatedExpression',
  categoryTag: `${PRIVATE_EXTENSION_BASE_URL}/category-tag`,
  dataType: `${PRIVATE_EXTENSION_BASE_URL}/data-type`,
  disabledDisplay: `${PRIVATE_EXTENSION_BASE_URL}/disabled-display`,
  groupType: `${PRIVATE_EXTENSION_BASE_URL}/group-type`,
  hideControlLabel: `${PRIVATE_EXTENSION_BASE_URL}/hide-control-label`,
  infoText: `${PRIVATE_EXTENSION_BASE_URL}/information-text`,
  inputWidth: `${PRIVATE_EXTENSION_BASE_URL}/input-width`,
  minRows: `${PRIVATE_EXTENSION_BASE_URL}/text-min-rows`,
  preferredElement: `${PRIVATE_EXTENSION_BASE_URL}/preferred-element`,
  secondaryInfoText: `${PRIVATE_EXTENSION_BASE_URL}/information-text-secondary`,
  validateAgeOver: `${PRIVATE_EXTENSION_BASE_URL}/validate-age-over`,
  requiredBooleanValue: `${PRIVATE_EXTENSION_BASE_URL}/permissible-value`,
  // complex extensions
  answerLoadingOptions: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/answer-loading-options`,
    strategy: `${PRIVATE_EXTENSION_BASE_URL}/strategy`,
    source: `${PRIVATE_EXTENSION_BASE_URL}/source`,
    expression: `${PRIVATE_EXTENSION_BASE_URL}/expression`,
  },
  complexValidation: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation`,
    type: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation-type`,
    triggerWhen: {
      extension: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation-triggerWhen`,
      question: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation-triggerQuestion`,
      operator: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation-triggerOperator`,
      answer: `${PRIVATE_EXTENSION_BASE_URL}/complex-validation-triggerAnswer`,
    },
  },
  filterWhen: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/filter-when`,
    question: `${PRIVATE_EXTENSION_BASE_URL}/filter-when-question`,
    operator: `${PRIVATE_EXTENSION_BASE_URL}/filter-when-operator`,
    answer: `${PRIVATE_EXTENSION_BASE_URL}/filter-when-answer`,
  },
  requireWhen: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/require-when`,
    question: `${PRIVATE_EXTENSION_BASE_URL}/require-when-question`,
    operator: `${PRIVATE_EXTENSION_BASE_URL}/require-when-operator`,
    answer: `${PRIVATE_EXTENSION_BASE_URL}/require-when-answer`,
  },
  textWhen: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/text-when`,
    question: `${PRIVATE_EXTENSION_BASE_URL}/text-when-question`,
    operator: `${PRIVATE_EXTENSION_BASE_URL}/text-when-operator`,
    answer: `${PRIVATE_EXTENSION_BASE_URL}/text-when-answer`,
    substituteText: `${PRIVATE_EXTENSION_BASE_URL}/text-when-substitute-text`,
  },
  answerDisplayFilter: {
    extension: `${PRIVATE_EXTENSION_BASE_URL}/answer-display-filter`,
    question: `${PRIVATE_EXTENSION_BASE_URL}/answer-display-filter-question`,
    operator: `${PRIVATE_EXTENSION_BASE_URL}/answer-display-filter-operator`,
    answer: `${PRIVATE_EXTENSION_BASE_URL}/answer-display-filter-answer`,
    include: `${PRIVATE_EXTENSION_BASE_URL}/answer-display-filter-include`,
  },
};

// https://hl7.org/fhir/R4B/valueset-audit-event-outcome.html
export const AUDIT_EVENT_OUTCOME_CODE = {
  success: '0',
  minorFailure: '4',
  seriousFailure: '8',
  majorFailure: '12',
};

export const ACCOUNT_PAYMENT_PROVIDER_ID_SYSTEM_STRIPE = 'https://api.stripe.com/v1/customers';
export const ACCOUNT_PAYMENT_PROVIDER_ID_SYSTEM_STRIPE_ACCOUNT = 'https://api.stripe.com/v1/accounts';
export const SCHEDULE_OWNER_STRIPE_ACCOUNT_EXTENSION_URL = 'https://fhir.ottehr.com/Extension/stripe-account-id';
/** @deprecated Use Device resource with STRIPE_TERMINAL_LOCATION_DEVICE_TYPE_SYSTEM and STRIPE_TERMINAL_LOCATION_DEVICE_TYPE_CODE instead */
export const SCHEDULE_OWNER_ADVAPACS_LOCATION_EXTENSION_URL = 'https://fhir.ottehr.com/Extension/advapacs-location-id';

// Device-based terminal location storage
export const STRIPE_TERMINAL_LOCATION_IDENTIFIER_SYSTEM = 'https://api.stripe.com/v1/terminal/locations';
export const STRIPE_TERMINAL_LOCATION_DEVICE_TYPE_SYSTEM = 'https://fhir.ottehr.com/CodeSystem/device-type';
export const STRIPE_TERMINAL_LOCATION_DEVICE_TYPE_CODE = 'stripe-terminal-config';

export const WALKIN_APPOINTMENT_TYPE_CODE = 'WALKIN';
export const SLOT_WALKIN_APPOINTMENT_TYPE_CODING: CodeableConcept = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
      code: WALKIN_APPOINTMENT_TYPE_CODE,
    },
  ],
};

export const FOLLOW_UP_APPOINTMENT_TYPE_CODE = 'FOLLOWUP';
export const SLOT_POST_TELEMED_APPOINTMENT_TYPE_CODING: CodeableConcept = {
  coding: [
    {
      system: 'http://terminology.hl7.org/CodeSystem/v2-0276',
      code: 'FOLLOWUP',
    },
  ],
};

export enum ServiceModeCategoryCode {
  virtualServiceMode = 'virtual-service-mode',
  inPersonServiceMode = 'in-person-service-mode',
}

export const SlotServiceCategory: { [key: string]: CodeableConcept } = {
  virtualServiceMode: {
    coding: [
      {
        system: `${FHIR_BASE_URL}/slot-service-category`,
        code: ServiceModeCategoryCode.virtualServiceMode,
      },
      // added to avoid confusion with new service category code system
      // can be removed in the future
      {
        system: `${OTTEHR_CODE_SYSTEM_BASE_URL}/service-mode-service-category`,
        code: ServiceModeCategoryCode.virtualServiceMode,
      },
    ],
  },
  inPersonServiceMode: {
    coding: [
      {
        system: `${FHIR_BASE_URL}/slot-service-category`,
        code: ServiceModeCategoryCode.inPersonServiceMode,
      },
      {
        system: `${OTTEHR_CODE_SYSTEM_BASE_URL}/service-mode-service-category`,
        code: ServiceModeCategoryCode.inPersonServiceMode,
      },
    ],
  },
};

export const SLOT_BOOKING_FLOW_ORIGIN_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/slot-booking-flow-origin`;

export const makeBookingOriginExtensionEntry = (url: string): { url: string; valueString: string } => {
  return {
    url: SLOT_BOOKING_FLOW_ORIGIN_EXTENSION_URL,
    valueString: url,
  };
};

// Extension recording the Location a Slot is being offered at. Stamped at
// slot-vending time so the Slot is self-describing — create-appointment
// reads this rather than re-resolving from the Schedule.actor graph (which
// is ambiguous for multi-location PractitionerRoles).
export const SLOT_AT_LOCATION_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/slot-at-location`;

export const makeSlotAtLocationExtensionEntry = (
  locationId: string
): { url: string; valueReference: { reference: string } } => {
  return {
    url: SLOT_AT_LOCATION_EXTENSION_URL,
    valueReference: { reference: `Location/${locationId}` },
  };
};

// Extension recording the group HealthcareService a Slot was booked through.
// Stamped at slot-vending time when the scheduleType is "group" — the
// Slot's Schedule.actor under pools-providers is the member PR, so
// without this extension a downstream consumer can't tell whether a
// PR-actored Slot was booked directly against that PR or through a group
// (which matters for assignment-mode interpretation, capacity-guard
// fallback eligibility, audit trails, etc.). Skipped when the Schedule's
// actor IS the group HS — the actor already records it; a redundant
// extension can only conflict.
export const SLOT_BOOKED_VIA_GROUP_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/slot-booked-via-group`;

export const makeSlotBookedViaGroupExtensionEntry = (
  groupHealthcareServiceId: string
): { url: string; valueReference: { reference: string } } => {
  return {
    url: SLOT_BOOKED_VIA_GROUP_EXTENSION_URL,
    valueReference: { reference: `HealthcareService/${groupHealthcareServiceId}` },
  };
};

// Meta tag stamped on a Slot when the create-appointment capacity-guard
// fallback rerouted it from its originally-targeted Schedule to a different
// member Schedule of the same anonymous-mode group. Bare boolean tag — purely
// a queryability handle ("how often does this happen, which Slots had it
// happen to them"). The original Schedule reference is recoverable from the
// Slot's FHIR _history if ever needed.
export const SLOT_FALLBACK_REROUTED_TAG_SYSTEM = `${PRIVATE_EXTENSION_BASE_URL}/slot-fallback-rerouted`;
export const SLOT_FALLBACK_REROUTED_TAG = {
  system: SLOT_FALLBACK_REROUTED_TAG_SYSTEM,
  code: 'true',
};

// Extension for specifying which questionnaire should be used for appointments booked on this slot
export const SLOT_QUESTIONNAIRE_CANONICAL_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/slot-questionnaire-canonical`;

export const makeQuestionnaireCanonicalExtensionEntry = (
  canonical: CanonicalUrl
): { url: string; valueString: string } => {
  // Store as "url|version" format (standard FHIR canonical with version)
  const canonicalString = `${canonical.url}|${canonical.version}`;
  return {
    url: SLOT_QUESTIONNAIRE_CANONICAL_EXTENSION_URL,
    valueString: canonicalString,
  };
};

export const parseQuestionnaireCanonicalExtension = (valueString: string): CanonicalUrl => {
  const [url, version] = valueString.split('|');
  if (!version) {
    throw new Error(`Invalid questionnaire canonical extension value: "${valueString}" - missing version`);
  }
  return { url, version };
};

// this is the time in minutes after which a busy-tentative slot will be considered expired and will no longer
// be counted against the available slots. the _lastUpdated field will be checked, so mutating the slot
// at all will reset the timer
export const SLOT_BUSY_TENTATIVE_EXPIRATION_MINUTES = 10;
export const DEFAULT_APPOINTMENT_LENGTH_MINUTES = 15;

const PROCEDURES_TERMINOLOGY_BASE_URL = FHIR_BASE_URL + '/CodeSystem/Procedure';
export const PROCEDURE_TYPE_SYSTEM = PROCEDURES_TERMINOLOGY_BASE_URL + '/procedure-type';
export const PERFORMER_TYPE_SYSTEM = PROCEDURES_TERMINOLOGY_BASE_URL + '/performer-type';
export const BODY_SITE_SYSTEM = PROCEDURES_TERMINOLOGY_BASE_URL + '/body-site';

export const PAYMENT_METHOD_EXTENSION_URL = PUBLIC_EXTENSION_BASE_URL + '/payment-method';
// stamped on the original PaymentNotice when Stripe reports refunds against the payment,
// so refund state can be read from FHIR without going back to Stripe
export const PAYMENT_REFUNDS_EXTENSION_URL = PUBLIC_EXTENSION_BASE_URL + '/payment-refunds';
// stamped on a PaymentNotice when a non-processor payment (cash/check/external reader) is voided
export const PAYMENT_VOID_EXTENSION_URL = PUBLIC_EXTENSION_BASE_URL + '/payment-void';
// stamped on a billing PaymentNotice for a Stripe payment: the account the money was taken on, so
// refunds and lookups go to that account rather than one derived from the payee or schedule
export const PAYMENT_STRIPE_ACCOUNT_EXTENSION_URL = PUBLIC_EXTENSION_BASE_URL + '/payment-stripe-account';

export const PREFERRED_PHARMACY_EXTENSION_URL = ottehrExtensionUrl('preferred-pharmacy');
export const PREFERRED_PHARMACY_MANUAL_ENTRY_URL = ottehrExtensionUrl('pharmacy-manual-entry'); // added when the pharmacy was added manually via text fields
export const PREFERRED_PHARMACY_PLACES_ID_URL = ottehrExtensionUrl('pharmacy-places-id'); // added when the pharmacy was selected with places search

// docs.oystehr.com/oystehr/services/erx/patient-sync/#preferred-pharmacy
export const PREFERRED_PHARMACY_ERX_ID_FOR_SYNC_URL =
  'https://extensions.fhir.oystehr.com/patient/erx-preferred-pharmacy-id';

export const PRESCRIPTION_ERX_PHARMACY_ID_URL = 'https://extensions.fhir.oystehr.com/prescription/erx-pharmacy-id';

export const ENCOUNTER_PAYMENT_VARIANT_EXTENSION_URL = ottehrExtensionUrl('payment-variant');

/** Employer Organization selected for this visit (staff / pre-op); not the patient Account occ-med employer. */
export const ENCOUNTER_VISIT_OCCUPATIONAL_MEDICINE_EMPLOYER_EXTENSION_URL = ottehrExtensionUrl(
  'visit-occupational-medicine-employer'
);

export const CONSENT_ATTESTATION_SIG_TYPE: Coding = Object.freeze({
  system: 'http://uri.etsi.org/01903/v1.2.2',
  code: 'ProofOfReceipt',
});

export const TASK_CATEGORY_IDENTIFIER = ottehrIdentifierSystem('task-category');
export const TASK_INPUT_SYSTEM = ottehrCodeSystemUrl('task-input');
export const TASK_LOCATION_SYSTEM = ottehrCodeSystemUrl('task-location');
export const TASK_ASSIGNED_DATE_TIME_EXTENSION_URL = ottehrExtensionUrl('task-assigned-date-time');

export const RCM_TASK_SYSTEM = ottehrCodeSystemUrl('rcm-task');
// note: be careful, one of these codes are hardcoded in zambdas config file in SUB-SEND-INVOICE-TO-PATIENT endpoint
export enum RcmTaskCode {
  sendInvoiceToPatient = 'send-invoice-to-patient',
  sendInvoiceOutputInvoiceId = 'send-invoice-output-invoice-Id',
  sendInvoiceOutputError = 'send-invoice-output-error',
  stripeInvoiceStatus = 'send-invoice-output-stripe-status',
}
export const RcmTaskCodings: { [key: string]: CodeableConcept } = {
  sendInvoiceToPatient: {
    coding: [
      {
        system: RCM_TASK_SYSTEM,
        code: RcmTaskCode.sendInvoiceToPatient,
      },
    ],
  },
  sendInvoiceOutputInvoiceId: {
    coding: [
      {
        system: RCM_TASK_SYSTEM,
        code: RcmTaskCode.sendInvoiceOutputInvoiceId,
      },
    ],
  },
  sendInvoiceOutputError: {
    coding: [
      {
        system: RCM_TASK_SYSTEM,
        code: RcmTaskCode.sendInvoiceOutputError,
      },
    ],
  },
  stripeInvoiceStatus: {
    coding: [
      {
        system: RCM_TASK_SYSTEM,
        code: RcmTaskCode.stripeInvoiceStatus,
      },
    ],
  },
};

export const DOCUMENT_REFERENCE_SUMMARY_FROM_AUDIO = 'Summary of visit from audio recording';
export const DOCUMENT_REFERENCE_SUMMARY_FROM_CHAT = 'Summary of visit from chat';

export const AMBIENT_SCRIBE_RECORDING_PENDING_CODING = {
  system: `${OTTEHR_CODE_SYSTEM_BASE_URL}/document-type`,
  code: 'ambient-scribe-recording-pending',
  display: 'Ambient scribe recording pending transcription',
};

export const EMPLOYER_ORG_IDENTIFIER_SYSTEM = ottehrIdentifierSystem('organization-type');

export const ATTORNEY_FIRM_EXTENSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/attorney-firm`;

export const GLOBAL_TEMPLATE_META_TAG_CODE_SYSTEM = `${PRIVATE_EXTENSION_BASE_URL}/global-template-list`;
export const GLOBAL_TEMPLATE_IN_PERSON_CODE_SYSTEM = `${OTTEHR_CODE_SYSTEM_BASE_URL}/global-template-in-person`;

/** Builds the full meta.tag system URL from a chart data field name (e.g. 'chief-complaint' → full URL). */
export const chartDataTagSystem = (fieldName: string): string => `${PRIVATE_EXTENSION_BASE_URL}/${fieldName}`;

export const VIDEO_CHAT_WAITING_ROOM_NOTIFICATION_TASK_TYPE = ottehrCodeSystemUrl('task-type');
export const VIDEO_CHAT_WAITING_ROOM_NOTIFICATION_TASK_CODE = 'video-chat-waiting-room-notification';

export const ACCIDENT_TYPE_SYSTEM = ottehrCodeSystemUrl('accident-type');
export const ACCIDENT_STATE_EXTENSION = ottehrExtensionUrl('accident-state');

export const PROVENANCE_FAX_SYSTEM = ottehrCodeSystemUrl('faxes');
export const PROVENANCE_FAX_ACTIVITY_CODES = {
  faxSent: 'fax-sent',
} as const;
export const PROVENANCE_FAX_ACTIVITY_DISPLAY = {
  faxSent: 'Fax Sent',
} as const;
export const FAX_SENT_PROVENANCE_ACTIVITY_CODING: Coding = {
  code: PROVENANCE_FAX_ACTIVITY_CODES.faxSent,
  display: PROVENANCE_FAX_ACTIVITY_DISPLAY.faxSent,
  system: PROVENANCE_FAX_SYSTEM,
};

/** Identifier system Oystehr stamps on the Communication resources it creates for outbound faxes. */
export const OYSTEHR_FAX_COMMUNICATION_IDENTIFIER_SYSTEM = 'https://identifiers.oystehr.com/fax';
/** Extension on those Communications whose CodeableConcept code Oystehr updates as the fax progresses. */
export const OYSTEHR_OUTBOUND_FAX_STATUS_EXTENSION_URL = 'https://extensions.fhir.oystehr.com/outbound-fax-status';
export const OYSTEHR_OUTBOUND_FAX_STATUS_CODES = {
  delivered: 'DELIVERED',
  stopped: 'STOPPED',
} as const;

export const OUTBOUND_DELIVERY_TASK_SYSTEM = ottehrCodeSystemUrl('outbound-delivery');
export const OUTBOUND_DELIVERY_TASK_CODES = {
  fax: 'fax',
  email: 'email',
} as const;
export const OUTBOUND_DELIVERY_INPUT_SYSTEM = ottehrCodeSystemUrl('outbound-delivery-input');
export const OUTBOUND_DELIVERY_INPUT_CODES = {
  recipientAddress: 'recipient-address',
  recipientName: 'recipient-name',
  recipientOrganization: 'recipient-organization',
  recipientPhone: 'recipient-phone',
  documentReference: 'document-reference',
  senderId: 'sender-id',
  senderDisplay: 'sender-display',
  senderOrganization: 'sender-organization',
  faxPacketPageCount: 'fax-packet-page-count',
  faxPacketParts: 'fax-packet-parts',
} as const;
export const OUTBOUND_DELIVERY_OUTPUT_SYSTEM = ottehrCodeSystemUrl('outbound-delivery-output');
export const OUTBOUND_DELIVERY_OUTPUT_CODES = {
  communication: 'communication',
  error: 'error',
} as const;
export const OUTBOUND_DELIVERY_SOURCE_IDENTIFIER_SYSTEM = ottehrIdentifierSystem('outbound-delivery-source');
export const OUTBOUND_DELIVERY_RETRY_IDENTIFIER_SYSTEM = ottehrIdentifierSystem('outbound-delivery-retry');
export const OUTBOUND_DELIVERY_CLAIM_IDENTIFIER_SYSTEM = ottehrIdentifierSystem('outbound-delivery-claim');

export const EMPLOYEE_ID_SYSTEM = ottehrIdentifierSystem('employee-id');

export const CHARGE_MASTER_DESIGNATION_EXTENSION_URL = ottehrExtensionUrl('charge-master-designation');
export const RCM_TAG_SYSTEM = `${PRIVATE_EXTENSION_BASE_URL}/rcm`;
export type ChargeMasterDesignation = 'default-insurance' | 'self-pay';
export type FeeScheduleDesignation = 'case-rate';
export const CASE_RATE_CODE = 'case-rate';

export const CPT_MODIFIER_EXTENSION_URL = ottehrExtensionUrl('cpt-modifier');
export const CPT_BILLABLE_UNITS_EXTENSION_URL = ottehrExtensionUrl('cpt-billable-units');
export const CPT_CODE_SYSTEM = 'http://www.ama-assn.org/go/cpt';

export const EXAM_MIGRATION_VERSION_URL = `${PRIVATE_EXTENSION_BASE_URL}/exam-migration-version`;
// Version 1 and 2 are essentially the same
// The version was bumped to 2 to facilitate rendering a incompatibility message for old telemed charts
// Version 1 was only stamped on encounters where users clicked "migrate exam" however in order to differentiate between virtual visits pre and post exam config consolidation
// We need to record the current migration version on all encounters. There is an edge case were telemed exams were migrated and stamped with v1 before the 1.35 release
// but would actually be incompatible with the exam config going out in 1.35 (aka v2)
export const CURRENT_EXAM_MIGRATION_VERSION = 2;
export const INCOMPATIBLE_EXAM_VERSION_MESSAGE =
  "This chart's exam version is incompatible with the current exam configuration, please consult the visit PDF.";

export const BILLING_RESOURCE_TAG = {
  system: 'https://fhir.ottehr.com/billing/resource-type',
  code: 'billing-resource',
};

export const CHARGE_ITEM_DEFINITION_DEFAULT_SYSTEM = 'https://fhir.ottehr.com/billing/charge-item-definition-default';
